import { cn } from '@/app/_lib/utils';
import type { LogoSize } from './utils/logo-size';

/**
 * Partner-list layout matrix — the single source for "which layout for which case".
 *
 * There are two independent layout SYSTEMS, chosen by `logoSize`, and each is
 * available as grid and carousel (`displayMode`):
 *
 *   SMALL — compact cards in a fixed raster (legacy behaviour).
 *     • grid:     standalone → 1 / 2 / 4 columns; nested → 2-column placement.
 *     • carousel: standalone → 4-per-view; nested → 1 (mobile) / 2 (lg).
 *
 *   LARGE — big logos, no card surface.
 *     • grid:     few → centered natural-width row; many → box grid whose column
 *                 count mirrors the carousel per-view (so both modes match).
 *     • carousel: standalone few → natural width (slides only when narrow);
 *                 standalone many → 4-per-view;
 *                 nested → like standalone while the two-column is stacked, 2 once
 *                 it splits (lg), 3 from xl where the half column is wide enough.
 *
 * "few large" = a partial row that would otherwise sit left-aligned:
 * standalone ≤ 3, nested ≤ 2.
 */

export type DisplayMode = 'grid' | 'carousel';

export const usesCarousel = (displayMode: DisplayMode): boolean =>
  displayMode === 'carousel';

const isFewLargeLogos = (isNested: boolean, count: number): boolean =>
  isNested ? count <= 2 : count <= 3;

/* ── GRID ──────────────────────────────────────────────────────────────── */

export type GridVariant = 'centered' | 'box' | 'placement' | 'plain';

export function resolveGridVariant(
  logoSize: LogoSize,
  isNested: boolean,
  count: number,
): GridVariant {
  if (logoSize === 'large') {
    return isFewLargeLogos(isNested, count) ? 'centered' : 'box';
  }
  return isNested ? 'placement' : 'plain';
}

// Large + few: natural-width logos grouped and centered. flex-1 + content-center
// vertically centre them in a stretched (nested) column; a no-op for standalone.
export const GRID_CENTERED_ROW =
  'flex flex-1 flex-wrap content-center items-center justify-center gap-8';

// Large + many: mirror the carousel's per-view counts (viewport breakpoints) so
// grid and carousel show the same number of logos per row.
// - standalone: 1 / 2 / 3 / 4 (matches the 4-per-view carousel).
// - nested: 1 / 2 / 3 / 2 / 3 — matches the nested carousel, which drops back to
//   2 at lg (where the two-column splits) and returns to 3 at xl.
export function gridBoxClass(isNested: boolean): string {
  return isNested
    ? 'grid gap-8 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3'
    : 'grid gap-8 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
}

// Small raster columns. 'placement' pairs with getGridPlacement per item.
export const GRID_SMALL: Record<'plain' | 'placement', string> = {
  plain: 'grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  placement: 'grid gap-4 grid-cols-1 sm:grid-cols-2',
};

// Nested wrapper: fill the stretched column so the centered row can sit vertically
// centred. Only for the centered grid variant (empty otherwise / in carousel mode).
export const NESTED_CENTERED_WRAPPER = 'flex h-full flex-col';

/* ── CAROUSEL ──────────────────────────────────────────────────────────── */

// Slide width per system/context (see the module header for the reasoning).
export function carouselSlideClass(
  logoSize: LogoSize,
  isNested: boolean,
  count: number,
): string {
  if (isNested) {
    // large: stacked (< lg) like standalone (1 → 2 → 3); split (lg) → 2; xl → 3.
    // small: 1 on mobile, 2 from lg.
    return logoSize === 'large'
      ? 'basis-full pl-4 sm:basis-1/2 md:basis-1/3 lg:basis-1/2 xl:basis-1/3'
      : 'basis-full pl-4 lg:basis-1/2';
  }
  if (logoSize === 'large' && isFewLargeLogos(isNested, count)) {
    // Natural width: 1-per-view slider below md, grouped side by side from md.
    return 'basis-full pl-8 sm:basis-1/2 md:basis-auto';
  }
  // Small, and large 4+: plain 4-per-view raster.
  return 'basis-full pl-4 sm:basis-1/2 md:basis-1/3 lg:basis-1/4';
}

// Content-row class: the negative-margin gap offset (matching the slide's pl-*)
// plus the justify used where a partial row should be centered.
export function carouselRowClass(
  logoSize: LogoSize,
  isNested: boolean,
  count: number,
): string {
  const naturalLarge =
    !isNested && logoSize === 'large' && isFewLargeLogos(isNested, count);

  let justify = '';
  if (isNested) justify = count === 1 ? 'lg:justify-center' : '';
  else if (naturalLarge) justify = 'md:justify-center';

  return cn(naturalLarge ? '-ml-8' : '-ml-4', justify);
}

// Count/breakpoint-based control visibility (shown only where the row scrolls).
// Nested large is the exception — see carouselControlsUseRuntime.
export function carouselControlsClass(
  logoSize: LogoSize,
  isNested: boolean,
  count: number,
): string {
  if (count <= 1) return 'hidden';
  if (isNested) {
    // Small: 2 per view from lg (2 items fit there). Large: gated by runtime
    // canScroll instead (returns 'block' when shown).
    if (logoSize === 'large') return 'block';
    return count === 2 ? 'block lg:hidden' : 'block';
  }
  // Large under a full row (2–3) fit from md up → controls only below md.
  if (logoSize === 'large' && isFewLargeLogos(isNested, count)) return 'block md:hidden';
  if (count === 2) return 'block sm:hidden';
  if (count === 3) return 'block md:hidden';
  if (count === 4) return 'block lg:hidden';
  return 'block';
}

// Nested large's per-view count is non-monotonic across breakpoints (1/2/3/2/3),
// so count-based CSS can't tell when it scrolls — gate its controls on the runtime
// canScroll instead (no greyed-out arrows when everything fits).
export const carouselControlsUseRuntime = (
  logoSize: LogoSize,
  isNested: boolean,
): boolean => isNested && logoSize === 'large';
